import { useEffect, useRef, useState } from 'react'
import {
  Activity,
  Wrench,
  MessageSquare,
  ThumbsUp,
  AlertTriangle,
  Circle,
  Loader2,
  RefreshCw,
  ChevronDown,
} from 'lucide-react'
import type { ActivityEvent, ActivityEventType } from '@/types/dashboard'

interface ActivityFeedProps {
  events: ActivityEvent[]
  loading: boolean
  connected: boolean
  onRefresh?: () => void
}

type FilterType = 'all' | ActivityEventType

const eventIcons: Record<ActivityEventType, React.ElementType> = {
  tool_call: Wrench,
  session: MessageSquare,
  feedback: ThumbsUp,
  error: AlertTriangle,
  system: Activity,
}

const eventColors: Record<ActivityEventType, string> = {
  tool_call: 'text-blue-400',
  session: 'text-violet-400',
  feedback: 'text-emerald-400',
  error: 'text-red-400',
  system: 'text-text-muted',
}

function formatTime(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)

  if (diffSec < 60) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHour < 24) return `${diffHour}h ago`
  return date.toLocaleDateString()
}

function EventItem({ event }: { event: ActivityEvent }) {
  const Icon = eventIcons[event.type] || Activity
  const colorClass = eventColors[event.type] || 'text-text-muted'

  return (
    <div className="flex items-start gap-3 p-2 hover:bg-surface-hover rounded-lg transition-colors animate-in fade-in slide-in-from-top-1 duration-300">
      <div className={`p-1.5 rounded-lg bg-surface-hover ${colorClass}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {event.toolName && (
            <span className="text-xs font-mono text-accent truncate">
              {event.toolName}
            </span>
          )}
          {event.success !== undefined && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded ${
                event.success
                  ? 'bg-emerald-500/10 text-emerald-400'
                  : 'bg-red-500/10 text-red-400'
              }`}
            >
              {event.success ? 'OK' : 'FAIL'}
            </span>
          )}
          {event.durationMs !== undefined && (
            <span className="text-[10px] text-text-muted font-mono">
              {event.durationMs}ms
            </span>
          )}
        </div>

        <p className="text-xs text-text-muted truncate mt-0.5">
          {event.description}
        </p>
      </div>

      <span className="text-[10px] text-text-secondary whitespace-nowrap">
        {formatTime(event.timestamp)}
      </span>
    </div>
  )
}

export default function ActivityFeed({
  events,
  loading,
  connected,
  onRefresh,
}: ActivityFeedProps) {
  const [filter, setFilter] = useState<FilterType>('all')
  const [autoScroll, setAutoScroll] = useState(true)
  const listRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new events arrive
  useEffect(() => {
    if (autoScroll && listRef.current) {
      listRef.current.scrollTop = 0
    }
  }, [events, autoScroll])

  const filteredEvents =
    filter === 'all' ? events : events.filter((e) => e.type === filter)

  const filters: { key: FilterType; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'tool_call', label: 'Tools' },
    { key: 'feedback', label: 'Feedback' },
    { key: 'error', label: 'Errors' },
  ]

  return (
    <div className="bg-surface border border-surface-border rounded-xl overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-surface-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-accent" />
          <h3 className="text-sm font-semibold text-text">Live Activity</h3>
          {/* Connection indicator */}
          <div className="flex items-center gap-1.5">
            <Circle
              className={`w-2 h-2 ${
                connected
                  ? 'text-emerald-400 fill-emerald-400 animate-pulse'
                  : 'text-gray-500'
              }`}
            />
            <span className="text-[10px] text-text-muted">
              {connected ? 'Live' : 'Disconnected'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={loading}
              className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors"
              title="Refresh"
            >
              <RefreshCw
                className={`w-4 h-4 text-text-muted ${loading ? 'animate-spin' : ''}`}
              />
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="px-3 py-2 border-b border-surface-border flex gap-1">
        {filters.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-2 py-1 rounded text-xs transition-colors ${
              filter === key
                ? 'bg-accent/10 text-accent'
                : 'text-text-muted hover:bg-surface-hover'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Event List */}
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto p-2 space-y-1"
        onScroll={(e) => {
          const el = e.currentTarget
          setAutoScroll(el.scrollTop < 50)
        }}
      >
        {loading && events.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-accent" />
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-8 text-text-muted">
            <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No activity yet</p>
            <p className="text-xs mt-1">Events will appear here in real-time</p>
          </div>
        ) : (
          filteredEvents.map((event) => (
            <EventItem key={event.id} event={event} />
          ))
        )}
      </div>

      {/* Auto-scroll indicator */}
      {!autoScroll && events.length > 0 && (
        <button
          onClick={() => {
            setAutoScroll(true)
            if (listRef.current) listRef.current.scrollTop = 0
          }}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-1.5 bg-accent text-white text-xs rounded-full shadow-lg hover:bg-accent-hover transition-colors"
        >
          <ChevronDown className="w-3 h-3" />
          New events
        </button>
      )}
    </div>
  )
}
